import { FeatureLoader } from 'ol/featureloader';
import OLGeoJSONFormat from 'ol/format/GeoJSON';
import proj4 from 'proj4';
import { Extent } from 'ol/extent';
import Projection from 'ol/proj/Projection';
import VectorSource from 'ol/source/Vector';
import axios, { AxiosRequestConfig } from 'axios';
import { Map } from 'ol';
import { isExtent } from '../utils/map/extent';

export type LoadFeatureCollectionCallback = (
  config: AxiosRequestConfig,
) => Promise<GeoJSON.FeatureCollection>;

export class FeatureCollectionSourceLoaderDecorator {
  private _abortSignals = new Set<AbortController>();
  private _format = new OLGeoJSONFormat({
    dataProjection: 'EPSG:4326',
    featureProjection: this._map.getView().getProjection().getCode(),
  });

  constructor(
    private _source: VectorSource,
    private _map: Map,
    private _getGeoJSONFeatures: LoadFeatureCollectionCallback,
  ) {
    this._source.setLoader(this._loader);
  }

  protected _loader: FeatureLoader = async (
    extent,
    _,
    projection,
    success,
    failure,
  ) => {
    if (this._abortSignals.size > 0) {
      this._abortSignals.forEach((s) => s.abort());
      this._abortSignals = new Set();
    }

    const currentSignal = new AbortController();
    this._abortSignals.add(currentSignal);

    const params = this._buildParams(extent, projection);

    try {
      const response = await this._getGeoJSONFeatures({
        params,
        signal: currentSignal.signal,
      });

      const features = this._format.readFeatures(response);

      this._source.addFeatures(features);

      success?.(features);

      this._abortSignals.delete(currentSignal);

      return features;
    } catch (e) {
      if (!axios.isCancel(e)) {
        const fallbackMsg = `Не удалось загрузить объекты слоя новостей для экстента ${extent.toString()}`;
        const message = e instanceof Error ? e.message : fallbackMsg;

        console.error(message);
      }

      failure?.();
      this._abortSignals.delete(currentSignal);

      return [];
    }
  };

  private _buildParams = (extent: Extent, projection: Projection) => {
    const params = new URLSearchParams();

    const from = projection.getCode();
    const to = 'EPSG:4326';

    if (!isExtent(extent)) {
      throw new Error('Не удалось получить координаты экстента');
    }

    const [x1, y1, x2, y2] = extent;

    const firstPoint = proj4(from, to, [x1, y1]) as number[];
    const secondPoint = proj4(from, to, [x2, y2]) as number[];

    const firstX = firstPoint[0];
    const firstY = firstPoint[1];
    const secondX = secondPoint[0];
    const secondY = secondPoint[1];

    if (
      firstX === undefined ||
      firstY === undefined ||
      secondX === undefined ||
      secondY === undefined
    ) {
      throw new Error('Не удалось преобразовать координаты экстента');
    }

    params.set('_bbox', [firstX, firstY, secondX, secondY].toString());
    params.set('_srid', '4326');

    return params;
  };
}
